/**
 * Invoicing Invariants
 *
 * Permission matrix ported from the ERPs subdomain and adapted to the invoicing
 * command taxonomy:
 *   - `user` may open/close the add modal, select a connector, submit and delete.
 *   - `api`/`url` may set the registered-integrations list (and api may drive the
 *     submit/delete success/error lifecycle + close the modal).
 *   - `ai` is read-only: it cannot submit, delete, or set list data.
 *
 * The privileged provision trigger is `submit/start`: the AddInvoicingModal
 * "connect" click flips `submit/start` from the `user` source, which the
 * invoicing submit policy reacts to by calling `ProvisionSystemIntegration`.
 * Capability is DERIVED from the named client method via `commandOperations`,
 * exactly as `erps` gates `loading/setSubmitting`.
 */

import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import { InvoicingState } from './state';
import { InvoicingCommand } from './commands';

export const commandOperations: CommandOperationMap<InvoicingState, InvoicingCommand> = {
  'integrations/invoicing/submit/start': 'ProvisionSystemIntegration',
  // No deprovision command exists yet; gate deletion under the same
  // integration-write capability so it is not silently allowed unguarded.
  'integrations/invoicing/delete/start': 'ProvisionSystemIntegration'
};

export const invariants: Invariants<InvoicingState, InvoicingCommand> = {
  canApply: ({ state, command, meta }) => {
    const { type } = command;
    const { source } = meta;

    // API source — drives list/list lifecycle from policies.
    if ( source === 'api' ) {
      return (
        type === 'integrations/invoicing/list/set' ||
        type === 'integrations/invoicing/submit/success' ||
        type === 'integrations/invoicing/submit/error' ||
        type === 'integrations/invoicing/delete/success' ||
        type === 'integrations/invoicing/delete/error' ||
        type === 'integrations/invoicing/addModal/close'
      );
    }

    // AI source — read-only: no submit, delete, or list mutation.
    if ( source === 'ai' ) {
      if (
        type === 'integrations/invoicing/submit/start' ||
        type === 'integrations/invoicing/delete/start' ||
        type === 'integrations/invoicing/list/set'
      ) {
        return false;
      }
      return true;
    }

    // URL source — may set the registered-integrations list.
    if ( source === 'url' ) {
      return type === 'integrations/invoicing/list/set';
    }

    // User source — privileged provision trigger gated by commandOperations.
    if ( source === 'user' ) {
      if ( type === 'integrations/invoicing/list/set' ) return false;
      return gateCommandByOperation( commandOperations, { state, command, meta });
    }

    return false;
  }
};
